/**
 * Phase 4.4.3: Clinician Booking Requests Management UI
 *
 * Allows clinicians to view, approve, and reject booking requests
 * from patients (Public + Approval mode).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  UserIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import {
  bookingRequestAPI,
  PendingBookingRequest,
  PendingBookingRequestListResponse,
  RejectionReason,
} from '../../services/api';
import { formatDuration } from '../../utils/formatters';
import { formatRelativeBookingDate } from '../../utils/timezone';
import Card from '../ui/Card';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import Modal from '../ui/Modal';
import FormField, { Select, Textarea } from '../ui/FormField';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

interface RejectModalProps {
  isOpen: boolean;
  request: PendingBookingRequest | null;
  reasons: RejectionReason[];
  onClose: () => void;
  onReject: (requestId: number, reason: string, note?: string) => Promise<void>;
  isRejecting: boolean;
}

const RejectModal: React.FC<RejectModalProps> = ({
  isOpen,
  request,
  reasons,
  onClose,
  onReject,
  isRejecting,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [internalNote, setInternalNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setInternalNote('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || !selectedReason) return;
    await onReject(request.id, selectedReason, internalNote || undefined);
  };

  if (!request) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reject Booking Request">
      <div className="mb-4 p-3 bg-clinic-cream rounded-lg">
        <p className="text-sm font-medium text-clinic-dark">{request.patient_name}</p>
        <p className="text-sm text-clinic-muted">
          {(() => {
            const { weekday, date, time } = formatRelativeBookingDate(request.start_time);
            return `${weekday}, ${date} at ${time}`;
          })()}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <FormField label="Rejection Reason" htmlFor="rejectionReason" required helper="The client will see a professional message based on this reason.">
            <Select
              id="rejectionReason"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              placeholder="Select a reason..."
              options={reasons.map((reason) => ({
                value: reason.key,
                label: reason.message,
              }))}
            />
          </FormField>

          <FormField label="Internal Note (Optional)" htmlFor="internalNote" helper="Not shown to the client.">
            <Textarea
              id="internalNote"
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Private note for your records..."
            />
          </FormField>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            isLoading={isRejecting}
            disabled={!selectedReason}
          >
            {isRejecting ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface RequestCardProps {
  request: PendingBookingRequest;
  onApprove: (id: number) => void;
  onReject: (request: PendingBookingRequest) => void;
  isApproving: boolean;
}

const RequestCard: React.FC<RequestCardProps> = ({
  request,
  onApprove,
  onReject,
  isApproving,
}) => {
  const isPending = request.approval_status === 'pending';
  const isExpired = request.is_expired;
  const slotAvailable = request.slot_still_available;

  const formatDateTime = (dateStr: string) => {
    const { weekday, date, time } = formatRelativeBookingDate(dateStr);
    return {
      date: `${weekday}, ${date}`,
      time,
    };
  };

  const getTimeUntilExpiry = () => {
    const expiresAt = new Date(request.expires_at);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    if (diffMs <= 0) return 'Expired';
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m left`;
    }
    return `${diffHours}h left`;
  };

  const getStatusBadge = () => {
    if (request.approval_status === 'approved') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-primary/20 text-clinic-primary">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Approved
        </span>
      );
    }
    if (request.approval_status === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-accent/20 text-clinic-accent">
          <XCircleIcon className="h-3 w-3 mr-1" />
          Rejected
        </span>
      );
    }
    if (isExpired || request.approval_status === 'expired') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-muted/20 text-clinic-muted">
          <ClockIcon className="h-3 w-3 mr-1" />
          Expired
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-clinic-gold/30 text-clinic-dark">
        <ClockIcon className="h-3 w-3 mr-1" />
        Pending
      </span>
    );
  };

  const { date, time } = formatDateTime(request.start_time);

  return (
    <Card className="p-4 hover:shadow-clinic transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Patient info */}
          <div className="flex items-center mb-2">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-clinic-primary-lighter">
              <UserIcon className="h-5 w-5 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <h3 className="font-medium text-clinic-dark">{request.patient_name}</h3>
              <p className="text-sm text-clinic-muted">{request.patient_email}</p>
            </div>
          </div>

          {/* Appointment details */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-clinic-muted mb-2">
            <div className="flex items-center">
              <CalendarDaysIcon className="h-4 w-4 mr-1" />
              {date} at {time}
            </div>
            <div>{formatDuration(request.duration_minutes)}</div>
            <div>{request.num_attendees} attendee{request.num_attendees > 1 ? 's' : ''}</div>
          </div>

          {/* Notes */}
          {request.notes && (
            <p className="text-sm text-clinic-muted bg-clinic-cream/50 rounded px-2 py-1 mb-2 italic">
              "{request.notes}"
            </p>
          )}

          {/* Warnings */}
          {isPending && !isExpired && (
            <div className="flex items-center space-x-3 text-xs">
              {!slotAvailable && (
                <span className="flex items-center text-clinic-gold">
                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                  Slot no longer available
                </span>
              )}
              <span className="text-clinic-muted">Expires: {getTimeUntilExpiry()}</span>
            </div>
          )}

          {/* Rejection reason (for rejected requests) */}
          {request.approval_status === 'rejected' && request.rejection_reason && (
            <p className="text-xs text-clinic-muted mt-2">
              Reason: {request.rejection_message || request.rejection_reason}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end space-y-2 ml-4">
          {getStatusBadge()}

          {/* Action buttons for pending requests */}
          {isPending && !isExpired && (
            <div className="flex space-x-2 mt-2">
              <Button
                size="sm"
                onClick={() => onApprove(request.id)}
                disabled={isApproving || !slotAvailable}
                isLoading={isApproving}
                title={!slotAvailable ? 'Slot no longer available' : 'Approve this request'}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onReject(request)}
              >
                Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const BookingRequests: React.FC = () => {
  const [requests, setRequests] = useState<PendingBookingRequest[]>([]);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<PendingBookingRequest | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([]);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: PendingBookingRequestListResponse = await bookingRequestAPI.getClinicianRequests(
        includeResolved
      );
      setRequests(data.requests);
      setStats({
        pending: data.pending_count,
        approved: data.approved_count,
        rejected: data.rejected_count,
        expired: data.expired_count,
      });
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Failed to load booking requests'));
      console.error('Error fetching booking requests:', err);
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  const fetchRejectionReasons = useCallback(async () => {
    try {
      const data = await bookingRequestAPI.getRejectionReasons();
      setRejectionReasons(data.reasons);
    } catch (err) {
      console.error('Error fetching rejection reasons:', err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchRejectionReasons();
  }, [fetchRequests, fetchRejectionReasons]);

  const handleApprove = async (requestId: number) => {
    try {
      setApprovingId(requestId);
      const result = await bookingRequestAPI.approveRequest(requestId);
      toast.success(`Booking approved! Room: ${result.room_name || 'Assigned'}`);
      // Update local state
      setRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? { ...r, approval_status: 'approved', approval_responded_at: new Date().toISOString() }
            : r
        )
      );
      setStats((prev) => ({ ...prev, pending: prev.pending - 1, approved: prev.approved + 1 }));
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to approve request'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (requestId: number, reason: string, note?: string) => {
    try {
      setIsRejecting(true);
      await bookingRequestAPI.rejectRequest(requestId, reason, note);
      toast.success('Booking request rejected');
      // Update local state
      setRequests((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? {
                ...r,
                approval_status: 'rejected',
                rejection_reason: reason,
                approval_responded_at: new Date().toISOString(),
              }
            : r
        )
      );
      setStats((prev) => ({ ...prev, pending: prev.pending - 1, rejected: prev.rejected + 1 }));
      setRejectingRequest(null);
    } catch (err: any) {
      toast.error(apiErrorMessage(err, 'Failed to reject request'));
    } finally {
      setIsRejecting(false);
    }
  };

  const pendingRequests = requests.filter(
    (r) => r.approval_status === 'pending' && !r.is_expired
  );
  const resolvedRequests = requests.filter(
    (r) => r.approval_status !== 'pending' || r.is_expired
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-clinic-dark font-body">Booking Requests</h1>
        <p className="text-clinic-muted mt-1">
          Review and respond to appointment requests from patients.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Pending</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : stats.pending}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-primary/20 rounded-full flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-clinic-primary" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Approved</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : stats.approved}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-accent/20 rounded-full flex items-center justify-center">
              <XCircleIcon className="h-6 w-6 text-clinic-accent" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Rejected</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : stats.rejected}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-clinic-muted/20 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-clinic-muted" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-clinic-muted">Expired</p>
              <p className="text-2xl font-semibold text-clinic-dark">
                {loading ? '--' : stats.expired}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4">
          <StatusBanner
            variant="error"
            title="Unable to load requests"
            message={error}
            actions={
              <button
                onClick={fetchRequests}
                className="text-sm text-clinic-accent hover:underline"
              >
                Try again
              </button>
            }
          />
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clinic-primary"></div>
        </div>
      ) : (
        <>
          {/* Pending Requests Section */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-clinic-dark mb-4 flex items-center">
              Pending Requests
              {pendingRequests.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-sm rounded-full bg-clinic-gold/30 text-clinic-dark">
                  {pendingRequests.length}
                </span>
              )}
            </h2>

            {pendingRequests.length === 0 ? (
              <Card className="text-center py-8">
                <InboxIcon className="mx-auto h-12 w-12 text-clinic-muted" />
                <h3 className="mt-2 text-sm font-medium text-clinic-dark">No pending requests</h3>
                <p className="mt-1 text-sm text-clinic-muted">
                  New booking requests from patients will appear here.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    onApprove={handleApprove}
                    onReject={setRejectingRequest}
                    isApproving={approvingId === request.id}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Resolved Requests Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-clinic-dark">History</h2>
              <label className="flex items-center text-sm text-clinic-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeResolved}
                  onChange={(e) => setIncludeResolved(e.target.checked)}
                  className="h-4 w-4 rounded border-clinic-primary-light text-clinic-primary focus:ring-clinic-primary mr-2"
                />
                Show resolved requests
              </label>
            </div>

            {includeResolved && resolvedRequests.length > 0 && (
              <div className="space-y-3">
                {resolvedRequests.map((request) => (
                  <RequestCard
                    key={request.id}
                    request={request}
                    onApprove={handleApprove}
                    onReject={setRejectingRequest}
                    isApproving={false}
                  />
                ))}
              </div>
            )}

            {includeResolved && resolvedRequests.length === 0 && (
              <p className="text-sm text-clinic-muted text-center py-4">No resolved requests yet.</p>
            )}
          </div>
        </>
      )}

      {/* Reject Modal */}
      <RejectModal
        isOpen={!!rejectingRequest}
        request={rejectingRequest}
        reasons={rejectionReasons}
        onClose={() => setRejectingRequest(null)}
        onReject={handleReject}
        isRejecting={isRejecting}
      />
    </div>
  );
};

export default BookingRequests;
